import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Star, ExternalLink, ArrowLeft, FileText, Hash, Tag, Calendar,
  Globe2, MessageSquare, Code2, Repeat, ShoppingBag, Loader2,
} from "lucide-react";

export default function ClientProductReviewDetailPage() {
  const { clientId, eventId } = useParams();
  const [evt, setEvt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRaw, setShowRaw] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(
          `/clients/${clientId}/productreview/events/${eventId}`,
        );
        setEvt(data);
      } catch (e) {
        setError(e.response?.data?.detail || "Failed to load review");
      } finally { setLoading(false); }
    })();
  }, [clientId, eventId]);

  if (loading) {
    return (
      <div className="p-8 text-sm text-zinc-500 flex items-center gap-2"
        data-testid="pr-detail-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading review…
      </div>
    );
  }
  if (error || !evt) {
    return (
      <div className="p-8" data-testid="pr-detail-error">
        <Link to={`/clients/${clientId}/product-review`}
          className="inline-flex items-center gap-1 text-sm text-[#002FA7] hover:underline">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to ProductReview
        </Link>
        <div className="mt-6 rounded-sm border border-red-200 bg-red-50 p-4 text-sm text-red-900">
          {error || "Review not found"}
        </div>
      </div>
    );
  }

  const rating = Math.max(0, Math.min(5, Math.round(Number(evt.rating) || 0)));
  const submitted = evt.submitted_at || evt.received_at;
  const modified = evt.modified_at;
  const qsp = evt.query_string_parameters || {};
  const listing = evt.listing || {};
  const followUps = evt.follow_ups || [];

  return (
    <div className="space-y-5" data-testid="pr-detail-page">
      {/* Breadcrumb / back */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link to={`/clients/${clientId}/product-review`}
          className="inline-flex items-center gap-1 text-sm text-[#002FA7] hover:underline"
          data-testid="pr-detail-back">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to ProductReview
        </Link>
        {evt.review_url && (
          <a href={evt.review_url} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-sm text-[#002FA7] hover:underline"
            data-testid="pr-detail-open-external">
            View on ProductReview.com.au <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>

      {/* Header — review headline */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-6">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="h-14 w-14 rounded-sm bg-amber-500 text-white grid place-items-center shrink-0">
              <Star className="h-6 w-6 fill-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="label-overline">ProductReview · {evt.event || "review"}</div>
              <h1 className="font-display font-extrabold text-3xl tracking-tight mt-1">
                {evt.author_display_name || "Anonymous"}
              </h1>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <div className="flex items-center gap-0.5">
                  {[1,2,3,4,5].map((n) => (
                    <Star key={n}
                      className={`h-4 w-4 ${n <= rating ? "fill-amber-500 text-amber-500" : "text-zinc-300"}`} />
                  ))}
                </div>
                <span className="font-display font-bold text-base">{rating}.0</span>
                <span className="text-zinc-300">·</span>
                <span className="text-[12px] font-mono text-zinc-500">
                  {submitted ? new Date(submitted).toLocaleString() : "—"}
                </span>
                {modified && modified !== submitted && (
                  <>
                    <span className="text-zinc-300">·</span>
                    <span className="text-[12px] font-mono text-zinc-500">
                      edited {new Date(modified).toLocaleDateString()}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          {evt.review_body && (
            <div className="mt-5 border-t border-zinc-100 pt-5">
              <div className="label-overline mb-2 flex items-center gap-1">
                <MessageSquare className="h-3 w-3" /> Review body
              </div>
              <p className="text-base text-zinc-800 whitespace-pre-line leading-relaxed"
                data-testid="pr-detail-body">
                {evt.review_body}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Two-column meta */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Order context */}
        <Card className="rounded-sm border-zinc-200">
          <CardContent className="p-5">
            <div className="label-overline mb-3 flex items-center gap-1">
              <ShoppingBag className="h-3 w-3" /> Order &amp; listing
            </div>
            <MetaRow label="Invoice number" value={evt.invoice_number || "—"} mono />
            <MetaRow label="Listing" value={evt.listing_full_name || listing.fullName || "—"} />
            {listing.url && (
              <MetaRow label="Listing URL" value={
                <a href={listing.url} target="_blank" rel="noreferrer"
                  className="text-[#002FA7] hover:underline break-all">
                  {listing.url}
                </a>
              } />
            )}
            <MetaRow label="Brand external IDs"
              value={(evt.brand_external_entry_ids || []).join(", ") || "—"}
              mono />
            <MetaRow label="Submitted" value={submitted ? new Date(submitted).toLocaleString() : "—"} mono />
            <MetaRow label="Modified" value={modified ? new Date(modified).toLocaleString() : "—"} mono />
            <MetaRow label="Received by us" value={evt.received_at ? new Date(evt.received_at).toLocaleString() : "—"} mono />
            <MetaRow label="Event type" value={evt.event || "review"} mono />
          </CardContent>
        </Card>

        {/* Attribution */}
        <Card className="rounded-sm border-zinc-200">
          <CardContent className="p-5">
            <div className="label-overline mb-3 flex items-center gap-1">
              <Tag className="h-3 w-3" /> Attribution / source parameters
            </div>
            {Object.keys(qsp).length === 0 ? (
              <div className="text-sm text-zinc-400 italic">
                No source parameters captured for this review.
              </div>
            ) : (
              <div className="space-y-1.5">
                {Object.entries(qsp).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between gap-3 text-[12px] font-mono">
                    <span className="text-zinc-500">{k}</span>
                    <Badge variant="outline" className="rounded-sm font-mono text-[11px] break-all">
                      {String(v)}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
            {qsp.cmegi && (
              <div className="mt-3 rounded-sm bg-zinc-50 border border-zinc-200 p-2 text-[11px] text-zinc-600">
                <strong className="text-zinc-800">cmegi</strong> is the ProductReview channel
                tag — typically tells you which campaign / email / page sent the customer to leave the review.
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Follow-ups */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-5">
          <div className="label-overline mb-3 flex items-center gap-1">
            <Repeat className="h-3 w-3" /> Customer follow-ups ({followUps.length})
          </div>
          {followUps.length === 0 ? (
            <div className="text-sm text-zinc-400 italic">
              No follow-up reviews yet. ProductReview asks customers to update their review at 3 and 6 months;
              if they do, those updates will appear here.
            </div>
          ) : (
            <div className="space-y-3">
              {followUps.map((f, i) => {
                const fr = Math.max(0, Math.min(5, Math.round(Number(f.rating ?? f.starRating) || 0)));
                const body = f.body || f.reviewBody || f.comment || "";
                const ts = f.submittedAt || f.modifiedAt || f.createdAt;
                // Composite key — follow-ups don't have stable ids from PR;
                // (timestamp + body slice) is unique enough for the list.
                const stableKey = `${ts || "no-ts"}-${(body || "").slice(0, 24)}-${i}`;
                return (
                  <div key={stableKey} className="rounded-sm border border-zinc-200 p-3"
                    data-testid={`pr-detail-followup-${i}`}>
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <div className="flex items-center gap-0.5">
                        {[1,2,3,4,5].map((n) => (
                          <Star key={n}
                            className={`h-3 w-3 ${n <= fr ? "fill-amber-500 text-amber-500" : "text-zinc-300"}`} />
                        ))}
                      </div>
                      <span className="font-semibold text-sm">{fr}.0</span>
                      {ts && (
                        <span className="text-[11px] font-mono text-zinc-500">
                          · {new Date(ts).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    {body && (
                      <p className="text-sm text-zinc-700 whitespace-pre-line">{body}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Raw payload (admin debugging) */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-5">
          <div className="flex items-center justify-between gap-3">
            <div className="label-overline flex items-center gap-1">
              <Code2 className="h-3 w-3" /> Raw payload (developer view)
            </div>
            <Button variant="outline" size="sm" className="rounded-sm h-7 text-[11px]"
              onClick={() => setShowRaw(!showRaw)}
              data-testid="pr-detail-toggle-raw">
              {showRaw ? "Hide" : "Show"} JSON
            </Button>
          </div>
          {showRaw && (
            <pre className="mt-3 text-[11px] font-mono bg-zinc-900 text-zinc-100 p-3 rounded-sm overflow-x-auto max-h-[400px]"
              data-testid="pr-detail-raw">
              {JSON.stringify(evt.raw || evt, null, 2)}
            </pre>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function MetaRow({ label, value, mono }) {
  return (
    <div className="grid grid-cols-[150px_1fr] gap-3 py-1.5 border-b border-zinc-100 last:border-b-0 items-start">
      <div className="label-overline pt-0.5">{label}</div>
      <div className={`text-sm ${mono ? "font-mono text-[12px]" : ""} text-zinc-800 break-words`}>
        {value}
      </div>
    </div>
  );
}
